import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getDeck, 
  getCards, 
  addCard, 
  updateDeckStats,
  updateDeck,
  deleteDeck,
  Card, 
  Deck 
} from '../services/deckService';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DeckForm from '../components/DeckForm';
import ImportCardsDialog from '../components/ImportCardsDialog';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import AddCardDialog from '../components/AddCardDialog';

const EditDeckPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { deckId } = useParams<{ deckId: string }>();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeck = async () => {
      if (deckId) {
        const deckData = await getDeck(deckId);
        if (deckData) {
          if (deckData.userId !== user?.uid) {
            setError('You do not have permission to edit this deck');
            setIsLoading(false);
            return;
          }
          setDeck(deckData);
          const cardsData = await getCards(deckId);
          setCards(cardsData);
        } else {
          setError('Deck not found');
        }
        setIsLoading(false);
      }
    };

    fetchDeck();
  }, [deckId, user]);

  if (!user) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/decks')}
            sx={{ mt: 2 }}
          >
            Back to Decks
          </Button>
        </Box>
      </Container>
    );
  }

  if (!deck) {
    return null;
  }

  const handleUpdateDeck = async (data: {
    title: string;
    description: string;
    sideNames: string[];
    tags: string[];
    isPublic: boolean;
  }) => {
    try {
      const success = await updateDeck(deckId!, data);
      if (success) {
        navigate(`/decks/${deckId}`);
      } else {
        setError('Failed to update deck');
      }
    } catch (error) {
      console.error('Error updating deck:', error);
      setError('Failed to update deck');
    }
  };

  const handleDeleteDeck = async () => {
    if (deckId) {
      const success = await deleteDeck(deckId);
      if (success) {
        navigate('/decks');
      }
    }
  };

  const handleImportCards = async (cards: string[][]) => {
    if (!deckId) return;
    
    try {
      for (const card of cards) {
        await addCard(deckId, card);
      }
      // Обновляем список карточек
      const updatedCards = await getCards(deckId);
      setCards(updatedCards);
      // Обновляем статистику колоды
      await updateDeckStats(deckId);
    } catch (error) {
      console.error('Error importing cards:', error);
    }
  };

  const handleAddCard = async (sides: string[]) => {
    if (deckId) {
      const newCard = await addCard(deckId, sides);
      if (newCard) {
        const updatedCards = await getCards(deckId);
        setCards(updatedCards);
        await updateDeckStats(deckId);
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/decks/${deckId}`)}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Typography variant="h4">Edit Deck</Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setIsDeleteDialogOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Delete Deck
          </Button>
        </Box>

        <DeckForm
          open={true}
          onClose={() => navigate(`/decks/${deckId}`)}
          onSave={handleUpdateDeck}
          initialData={{
            title: deck.title,
            description: deck.description,
            sideNames: deck.sideNames,
            tags: deck.tags,
            isPublic: deck.isPublic,
          }}
          deckId={deckId!}
        />

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: '100%',
            height: '2px',
            background: 'linear-gradient(90deg, #EF233C, transparent)',
          }
        }}>
          <Typography variant="h5">Cards</Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            alignItems: 'center',
            visibility: 'visible'
          }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setIsImportDialogOpen(true)}
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'secondary.dark',
                },
              }}
            >
              Импорт
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Add Card
            </Button>
          </Box>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${deck?.sideNames.length + 1}, 1fr)`,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                p: 2
              }}>
                {deck?.sideNames.map((sideName, index) => (
                  <Typography 
                    key={index} 
                    variant="subtitle1" 
                    sx={{ fontWeight: 'bold', px: 2 }}
                  >
                    {sideName}
                  </Typography>
                ))}
                <Typography 
                  variant="subtitle1" 
                  sx={{ fontWeight: 'bold', px: 2, textAlign: 'center' }}
                >
                  Actions
                </Typography>
              </Box>
              {cards.map(card => (
                <Box 
                  key={card.id}
                  sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: `repeat(${deck?.sideNames.length + 1}, 1fr)`,
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  {card.sides.map((side, index) => (
                    <Typography 
                      key={index} 
                      sx={{ px: 2, py: 1 }}
                    >
                      {side.content}
                    </Typography>
                  ))}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        // TODO: Implement edit functionality
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        // TODO: Implement delete functionality
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        <AddCardDialog
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSave={handleAddCard}
          sideNames={deck?.sideNames || []}
        />

        <ImportCardsDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onImport={handleImportCards}
          sideCount={deck?.sideNames.length || 0}
          sideNames={deck?.sideNames || []}
        />

        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Deck</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this deck? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteDeck}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EditDeckPage; 